import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ImageOff, Plus, RefreshCw, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useBusinesses } from '@/features/business/use-businesses';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { InfoCardEditor } from './info-card-editor';
import { useInfoCards } from './info-cards.api';
import {
  daysRemaining,
  emptyCard,
  statusOf,
  type CardStatus,
  type InfoCard,
} from './types';

/**
 * Info cards — the promotional panels the app and web front show.
 *
 * The original printed three counts above the table (live, planned, completed)
 * and gave you no way to act on them: to see the live ones you read the status
 * column and scrolled. They are filter buttons here, which is what everyone was
 * using them as.
 *
 * Its status also came from the date window alone, so a switched-off card still
 * read "Live" — and the remaining-days figure was
 * `endDate.diff(today, t('days'))`, a *translated* string passed as a moment
 * unit. "Days" happens to normalise in English; "Días" does not, so a Spanish
 * reader was told a card had 1,209,600,000 days left. `statusOf` and
 * `daysRemaining` in ./types answer both properly.
 */

const ANY = 'ANY';

const STATUS_TONE: Record<CardStatus, 'success' | 'secondary' | 'outline'> = {
  live: 'success',
  planned: 'secondary',
  completed: 'outline',
  inactive: 'outline',
};

export function InfoCardsPage() {
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.resolvedLanguage ?? 'en') === 'en';

  const [draft, setDraft] = useState('');
  const [status, setStatus] = useState<string>(ANY);
  const [editing, setEditing] = useState<InfoCard | null>(null);

  const list = useInfoCards();
  const { data: businesses } = useBusinesses();

  const cards = useMemo(() => list.data ?? [], [list.data]);

  const counts = useMemo(() => {
    const tally: Record<CardStatus, number> = {
      live: 0,
      planned: 0,
      completed: 0,
      inactive: 0,
    };
    for (const card of cards) tally[statusOf(card)] += 1;
    return tally;
  }, [cards]);

  const term = draft.trim().toLowerCase();
  const rows = useMemo(
    () =>
      cards.filter((card) => {
        if (status !== ANY && statusOf(card) !== status) return false;
        if (!term) return true;
        // Searched over every name a reader might recognise it by, since the
        // list has no server-side search to defer to.
        return [card.name, card.titleEng, card.titleSpa]
          .filter(Boolean)
          .some((value) => value!.toLowerCase().includes(term));
      }),
    [cards, status, term]
  );

  const storeName = (card: InfoCard) =>
    card.businessId
      ? ((businesses ?? []).find((b) => b.id === card.businessId)?.name ??
        t('infoCards.oneStore'))
      : t('infoCards.allStores');

  const title = (card: InfoCard) =>
    (isEnglish ? card.titleEng : card.titleSpa) ||
    card.titleEng ||
    card.titleSpa ||
    '';

  const preview = (card: InfoCard) =>
    (isEnglish ? card.imageUrl : card.imageUrl2) ||
    card.imageUrl ||
    card.imageUrl2;

  const fields: FilterField[] = [
    {
      id: 'status',
      label: t('infoCards.status'),
      value: status,
      onChange: setStatus,
      sort: false,
      resetValue: ANY,
      options: [
        { value: ANY, label: t('infoCards.statusAny') },
        {
          value: 'live',
          label: `${t('infoCards.statusLive')} (${counts.live})`,
        },
        {
          value: 'planned',
          label: `${t('infoCards.statusPlanned')} (${counts.planned})`,
        },
        {
          value: 'completed',
          label: `${t('infoCards.statusCompleted')} (${counts.completed})`,
        },
        {
          value: 'inactive',
          label: `${t('infoCards.statusInactive')} (${counts.inactive})`,
        },
      ],
    },
  ];

  const StatusCell = ({ card }: { card: InfoCard }) => {
    const state = statusOf(card);
    const left = state === 'live' ? daysRemaining(card) : null;
    return (
      <div className="space-y-0.5">
        <Badge variant={STATUS_TONE[state]}>
          {t(`infoCards.status${state[0].toUpperCase()}${state.slice(1)}`)}
        </Badge>
        {left !== null && (
          <p className="text-muted-foreground text-xs">
            {t('infoCards.daysLeft', { count: left })}
          </p>
        )}
      </div>
    );
  };

  const Thumb = ({ card }: { card: InfoCard }) => {
    const src = preview(card);
    return src ? (
      <img
        src={src}
        alt=""
        className="bg-muted size-12 shrink-0 rounded-lg object-cover"
      />
    ) : (
      <div className="bg-muted text-muted-foreground flex size-12 shrink-0 items-center justify-center rounded-lg">
        <ImageOff className="size-4" />
      </div>
    );
  };

  const Where = ({ card }: { card: InfoCard }) => (
    <div className="flex flex-wrap gap-1">
      {card.showInApp && (
        <Badge variant="secondary">{t('infoCards.app')}</Badge>
      )}
      {card.showInWeb && (
        <Badge variant="secondary">{t('infoCards.web')}</Badge>
      )}
      {!card.showInApp && !card.showInWeb && (
        <span className="text-muted-foreground text-xs">
          {t('infoCards.nowhere')}
        </span>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('infoCards.title')}
        description={t('infoCards.subtitle')}
        actions={
          <>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t('common.reload')}
              disabled={list.isFetching}
              onClick={() => void list.refetch()}
            >
              <RefreshCw className={list.isFetching ? 'animate-spin' : ''} />
            </Button>
            <Button size="sm" onClick={() => setEditing(emptyCard())}>
              <Plus />
              <span className="hidden sm:inline">{t('infoCards.newCard')}</span>
            </Button>
          </>
        }
      />

      <FilterBar
        busy={list.isFetching}
        search={
          <div className="relative flex-1">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={t('infoCards.searchPlaceholder')}
              aria-label={t('infoCards.searchPlaceholder')}
              className="h-10 pl-9"
            />
          </div>
        }
        fields={fields}
        summary={
          list.isSuccess ? (
            <p className="text-muted-foreground text-sm" aria-live="polite">
              {t('infoCards.showingOf', {
                shown: rows.length,
                total: cards.length,
              })}
            </p>
          ) : null
        }
      />

      {list.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('infoCards.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void list.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {list.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <Skeleton className="size-12 shrink-0 rounded-lg" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-44" />
                <Skeleton className="h-3 w-60" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
          ))}
        </div>
      )}

      {list.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={list.isFetching} />
          <p
            className={cn(
              'text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm',
              list.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {cards.length === 0
              ? t('infoCards.none')
              : t('infoCards.noneMatch')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="hidden rounded-xl border lg:block">
            <Table busy={list.isFetching}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-16" />
                  <TableHead>{t('infoCards.name')}</TableHead>
                  <TableHead>{t('infoCards.store')}</TableHead>
                  <TableHead>{t('infoCards.window')}</TableHead>
                  <TableHead>{t('infoCards.shownIn')}</TableHead>
                  <TableHead>{t('infoCards.status')}</TableHead>
                  <TableHead className="w-20 text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((card) => (
                  <TableRow key={card.id ?? card.name}>
                    <TableCell>
                      <Thumb card={card} />
                    </TableCell>
                    <TableCell>
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {card.name || '—'}
                        </p>
                        {title(card) && (
                          <p className="text-muted-foreground truncate text-xs">
                            {title(card)}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="truncate">
                      {storeName(card)}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      <p>{formatDateTime(card.startDateTime)}</p>
                      <p className="text-muted-foreground">
                        {formatDateTime(card.endDateTime)}
                      </p>
                    </TableCell>
                    <TableCell>
                      <Where card={card} />
                    </TableCell>
                    <TableCell>
                      <StatusCell card={card} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => setEditing(card)}>
                        {t('infoCards.edit')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="relative space-y-2 lg:hidden">
            <BusyOverlay show={list.isFetching} />
            {rows.map((card) => (
              <div
                key={card.id ?? card.name}
                className={cn(
                  'bg-card space-y-3 rounded-xl border p-3',
                  list.isFetching && 'opacity-60 transition-opacity'
                )}
              >
                <div className="flex items-start gap-3">
                  <Thumb card={card} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{card.name || '—'}</p>
                    <p className="text-muted-foreground truncate text-xs">
                      {title(card) || storeName(card)}
                    </p>
                  </div>
                  <StatusCell card={card} />
                </div>
                <div className="text-muted-foreground text-xs">
                  {formatDateTime(card.startDateTime)} →{' '}
                  {formatDateTime(card.endDateTime)}
                </div>
                <Where card={card} />
                <Button
                  size="sm"
                  className="w-full"
                  onClick={() => setEditing(card)}
                >
                  {t('infoCards.edit')}
                </Button>
              </div>
            ))}
          </div>
        </>
      )}

      <InfoCardEditor
        card={editing}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      />
    </div>
  );
}
